import Image from "@/components/Image";
import { gdImgSrcPolicy } from "@/utils/imageUrl";
import { safeParse } from "@/utils/safeParse";
import { useEffect, useMemo, useRef } from "react";
import styles from "./index.module.less";

export function NavPopoverAd({
  ad,
  loopInterval = 5000,
}: {
  ad: IADData;
  loopInterval?: number;
}) {
  // 轮播图
  if (ad.material.type === "html") {
    const data = safeParse(ad.material.data as string);

    // 非json格式的字符串时，直接渲染html
    if (!data) {
      return (
        <div className={styles.adPopover}>
          <div
            dangerouslySetInnerHTML={{
              __html: ad.material.data as string,
            }}
            data-gd-click="resource_loc_click"
            data-gd-expose="resource_loc_expose"
            data-resource-type="html"
            data-resource-id={ad?.material?.id}
            data-resource-title={ad?.promotion?.name}
            data-content-source={ad?.position?.alias}
          ></div>
        </div>
      );
    }

    // json格式则进行轮播图渲染
    return (
      <div className={styles.adPopover}>
        <Carousel data={data} ad={ad} loopInterval={loopInterval} />
      </div>
    );
  }

  const materialData = ad?.material.data as ADMaterialData;

  const url = `//${materialData?.bucket_name}.huaban.com/${materialData?.bucket_key}`;

  const imageSrc = gdImgSrcPolicy(url, {
    displayHeight: 32, // 显示宽度
    width: ad?.material?.width, // 图片原始宽度
    height: ad?.material?.height, // 图片原始高度
    type: null, // 图片类型, 这里避免转换成webp，导致图片清晰度下降
  });

  return (
    <a
      className={styles.adPopover}
      href={ad.material.link}
      data-gd-click="resource_loc_click"
      data-gd-expose="resource_loc_expose"
      data-resource-type="图片"
      data-resource-id={ad?.material?.id}
      data-resource-title={ad?.promotion?.name}
      data-content-source={ad?.position?.alias}
      data-resource-func="广告"
      rel="nofollow noreferrer"
      target="_blank"
    >
      <Image {...imageSrc} alt={ad?.promotion?.name} height={32}></Image>
    </a>
  );
}

interface ICarouselData {
  imageUrl: string;
  link: string;
  name: string;
  type: string;
}

function Carousel({
  data,
  ad,
  loopInterval,
}: {
  data: ICarouselData[];
  ad: IADData;
  loopInterval: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const index = useRef(0);

  const renderItems = useMemo(() => {
    // 为了实现无缝轮播，将最后一个元素放到第一个元素前面
    return [...data, data[0]];
  }, [data]);

  // 轮播广告
  useEffect(() => {
    let timer: NodeJS.Timeout;

    function loop() {
      timer = setTimeout(() => {
        const height = ref.current.getBoundingClientRect().height;
        index.current++;

        ref.current.scrollTo({
          top: height * index.current,
          behavior: "smooth",
        });

        // 当最后一个时，先滚动到第一个，再滚动到第二个
        if (index.current >= data.length) {
          index.current = 0;
          // 延迟200ms，等待第一个滚动完成
          setTimeout(() => {
            ref.current.scrollTo({
              top: 0,
            });
          }, 200);
        }

        loop();
      }, loopInterval);
    }
    if (Array.isArray(data) && data.length > 1) {
      loop();
      return () => {
        clearTimeout(timer);
      };
    }
  }, [data, loopInterval]);

  return (
    <div className={styles.carousel} ref={ref}>
      {renderItems.map((item, index) => {
        return (
          <a
            href={item.link || ad.material.link}
            data-gd-click="resource_loc_click"
            data-gd-expose="resource_loc_expose"
            data-resource-type={item.type || "图片"}
            data-resource-id={ad?.material?.id}
            data-resource-title={item.name || ad?.promotion?.name}
            data-content-source={ad?.position?.alias}
            data-resource-func="广告"
            rel="nofollow noreferrer"
            target="_blank"
            key={index}
          >
            <Image
              {...gdImgSrcPolicy(item.imageUrl, {
                displayHeight: 32,
                type: null,
              })}
              alt={ad?.promotion?.name}
              height={32}
            ></Image>
          </a>
        );
      })}
    </div>
  );
}
